import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, LinearProgress, MenuItem, Select, Skeleton, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
  useMediaQuery, useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PremiumHeader from "./components/PremiumHeader";
import SummaryCards from "./components/SummaryCards";
import FilterPanel, { defaultFilter, FilterState } from "./components/FilterPanel";
import StaffRow from "./components/StaffRow";
import MobileCard from "./components/MobileCard";
import { Row } from "./types";
import { Meta, reportsApi } from "../shared/api";
import { useDebounced } from "../shared/useDebounced";

// Query params derived from filter state. Kept pure so effect dependencies
// remain stable and don't fan out into per-render fetches.
function toParams(f: FilterState, page: number, perPage: number) {
  return {
    search: f.search || undefined,
    capacity_status: f.capacity_status || undefined,
    study_mode: f.study_mode || undefined,
    day: f.day || undefined,
    start_time_from: f.start_time_from || undefined,
    start_time_to: f.start_time_to || undefined,
    staff_status: f.staff_status || undefined,
    class_status: f.class_status || undefined,
    min_usage: f.min_usage || undefined,
    max_usage: f.max_usage || undefined,
    page, per_page: perPage,
  };
}

const HEADERS = [
  "Staff", "Class", "Grade & Subjects", "Schedule", "Mode & Location",
  "Students", "Capacity", "Available", "Usage", "Status", "",
];

export default function StaffCapacityReportPage() {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [filter, setFilter] = useState<FilterState>({ ...defaultFilter });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Debounce the search box so a keystroke storm doesn't fan out into one
  // request per key — the spec asks for ~400 ms.
  const debouncedSearch = useDebounced(filter.search, 400);
  const effectiveFilter = useMemo(
    () => ({ ...filter, search: debouncedSearch }),
    [filter, debouncedSearch]
  );

  const listParams = useMemo(
    () => toParams(effectiveFilter, page, perPage),
    [effectiveFilter, page, perPage]
  );
  // Filter-only key so pagination changes never trigger the "filters changed"
  // reset-to-page-1 effect below.
  const filterKey = useMemo(
    () => JSON.stringify(toParams(effectiveFilter, 1, perPage)),
    [effectiveFilter, perPage]
  );

  const [rows, setRows] = useState<Row[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<null | "csv" | "pdf">(null);

  const reqRef = useRef(0);
  const tableTopRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    const token = ++reqRef.current;
    setLoading(true); setError(null);
    try {
      const res = await reportsApi.staffCapacity(listParams);
      if (token !== reqRef.current) return; // stale
      setRows(res.data as Row[]);
      setMeta(res.meta);
      setSummary(res.summary);
      setRefreshedAt(new Date());
    } catch (e: any) {
      if (token === reqRef.current) setError(e.message || String(e));
    } finally {
      if (token === reqRef.current) setLoading(false);
    }
  }, [listParams]);

  useEffect(() => { load(); }, [load]);

  // Any filter change → back to page 1. Split out from the params memo so it
  // doesn't fire a page-1 fetch on the OLD filter set.
  useEffect(() => { setPage(1); }, [filterKey]);

  const doExport = async (kind: "csv" | "pdf") => {
    setExporting(kind); setError(null);
    try {
      const fn = kind === "csv" ? reportsApi.staffCapacityCsv : reportsApi.staffCapacityPdf;
      await fn(toParams(effectiveFilter, 1, perPage));
    } catch (e: any) {
      setError(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(null);
    }
  };

  const gotoPage = (p: number) => {
    setPage(p);
    setTimeout(() => tableTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // Row action handlers — go to existing pages.
  const onViewStaff    = (id: number) => nav(`/team-history/${id}`);
  const onViewClass    = (id: number) => nav(`/class-detail/${id}`);
  const onViewStudents = (id: number) => nav(`/class-detail/${id}`);
  const onOpenSchedule = (id: number) => nav(`/class-detail/${id}`);

  const hasRows = rows.length > 0;

  return (
    <Box p={2}>
      <PremiumHeader
        refreshedAt={refreshedAt}
        loading={loading}
        exporting={exporting}
        onRefresh={load}
        onCsv={() => doExport("csv")}
        onPdf={() => doExport("pdf")}
      />

      <SummaryCards summary={summary} loading={loading && !hasRows} />

      <FilterPanel
        value={filter}
        onChange={setFilter}
        onClear={() => setFilter({ ...defaultFilter })}
        loading={loading}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", position: "relative" }}>
        {loading && (
          <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }} />
        )}

        {isMobile ? (
          <Box p={1}>
            {loading && !hasRows && [...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={220} sx={{ mb: 1 }} />
            ))}
            {!loading && !hasRows && <EmptyState onClear={() => setFilter({ ...defaultFilter })} />}
            {rows.map((r) => (
              <MobileCard key={`${r.class_id}-${r.schedule_id}`} row={r}
                onViewStaff={onViewStaff}
                onViewClass={onViewClass}
                onViewStudents={onViewStudents}
                onOpenSchedule={onOpenSchedule} />
            ))}
          </Box>
        ) : (
          <TableContainer sx={{ position: "relative" }}>
            <div ref={tableTopRef} />
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {HEADERS.map((h, i) => (
                    <TableCell key={i}
                      sx={{ fontWeight: 700, background: "#f8fafc", color: "#1b3a6b",
                        textTransform: "uppercase", fontSize: 11, letterSpacing: 0.4 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !hasRows && [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    {HEADERS.map((_h, j) => (
                      <TableCell key={j}><Skeleton height={26} /></TableCell>
                    ))}
                  </TableRow>
                ))}
                {!loading && !hasRows && (
                  <TableRow>
                    <TableCell colSpan={HEADERS.length}>
                      <EmptyState onClear={() => setFilter({ ...defaultFilter })} />
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <StaffRow key={`${r.class_id}-${r.schedule_id}`} row={r}
                    onViewStaff={onViewStaff}
                    onViewClass={onViewClass}
                    onViewStudents={onViewStudents}
                    onOpenSchedule={onOpenSchedule} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center"
          sx={{ p: 1.5, borderTop: "1px solid #eef2f7", background: "#fbfcfe" }}>
          <Typography variant="body2" color="text.secondary" flex={1}>
            {meta && meta.total > 0
              ? `Showing ${(meta.from ?? 0).toLocaleString()}–${(meta.to ?? 0).toLocaleString()} of ${meta.total.toLocaleString()} ${meta.total === 1 ? "class" : "classes"}`
              : "—"}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">Rows per page:</Typography>
            <Select size="small" value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              sx={{ minWidth: 80 }}>
              {[25, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
            <Button size="small" disabled={!meta || page <= 1 || loading}
              onClick={() => gotoPage(page - 1)}>Previous</Button>
            <Typography variant="body2">
              {meta ? `${meta.current_page} / ${meta.last_page}` : "—"}
            </Typography>
            <Button size="small" disabled={!meta || page >= (meta?.last_page ?? 1) || loading}
              onClick={() => gotoPage(page + 1)}>Next</Button>
          </Stack>
        </Stack>
      </Card>
    </Box>
  );
}

function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Stack alignItems="center" spacing={1} py={4}>
      <Typography fontWeight={600}>No classes found</Typography>
      <Typography variant="body2" color="text.secondary">
        Try changing or clearing the selected filters.
      </Typography>
      <Button variant="outlined" size="small" onClick={onClear}>Clear filters</Button>
    </Stack>
  );
}
